import { useCallback, useEffect, useState } from 'react'
import { Compteur } from '../Compteur'
import { calculerPaieLivreurs, chargerConfiguration, listerPaieLivreurs, marquerLivreursPayes } from '../../api/dispatch'
import type { PaieLivreur, PeriodePayee } from '../../api/types'
import { fcfa } from '../Format'
import { periodeAPayer, periodesDePaie, type PeriodePaie } from '../../api/paie'

/**
 * Paie des LIVREURS — par quinzaine.
 *
 * Les marchands n'ont plus d'écran ici : ils sont reversés le soir même de la livraison (écran « Reversements », 0065).
 * Les quatre primes du livreur restent séparées en quatre colonnes : fusionnées, une paie contestée devient invérifiable.
 */
/** Le montant d'une prime et, dessous, ce qui l'explique (« 6 courses », « 2 jours »). */
function Prime({ montant, detail }: { montant: number; detail: string | null }) {
  return (
    <>
      {fcfa(montant)}
      {detail && montant > 0 && (
        <div style={{ fontSize: 11, color: 'var(--n400)' }}>{detail}</div>
      )}
    </>
  )
}

const pluriel = (n: number, un: string, plusieurs: string) => (n > 0 ? `${n} ${n > 1 ? plusieurs : un}` : null)

const jourCourt = (d: string) => {
  try {
    return new Date(`${d}T00:00:00`).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })
  } catch {
    return d
  }
}

export function EcranPaie() {
  return <PaieLivreurs />
}

// ── Livreurs : quinzaine ─────────────────────────────────────────────────

function PaieLivreurs() {
  const [aPayer, setAPayer] = useState<PaieLivreur[]>([])
  const [historique, setHistorique] = useState<PeriodePayee[]>([])
  const [debut, setDebut] = useState('')
  const [fin, setFin] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  const [occupe, setOccupe] = useState(false)
  /* Les périodes se déduisent des deux jours de paie du réglage (1er et 16) : le
     dispatch n'a plus à les saisir à la main. `null` = réglage pas encore lu ou
     inexploitable ; la saisie manuelle reste alors la seule voie. */
  const [jours, setJours] = useState<[number, number] | null>(null)
  const [periodes, setPeriodes] = useState<PeriodePaie[]>([])

  useEffect(() => {
    let vivant = true
    void chargerConfiguration()
      .then((c) => {
        if (!vivant) return
        // Bamako = UTC : la « date du jour » ne dépend pas du fuseau du navigateur.
        const liste = periodesDePaie(c.jourPaie1, c.jourPaie2, new Date().toISOString().slice(0, 10), 4)
        setJours([c.jourPaie1, c.jourPaie2])
        setPeriodes(liste)
        const aPayer = periodeAPayer(liste)
        if (aPayer) {
          // Pré-remplissage : seulement si le dispatch n'a encore rien saisi.
          setDebut((d) => d || aPayer.debut)
          setFin((f) => f || aPayer.fin)
        }
      })
      .catch(() => undefined) // sans le réglage, on garde la saisie manuelle
    return () => {
      vivant = false
    }
  }, [])

  const charger = useCallback(async () => {
    try {
      const r = await listerPaieLivreurs()
      setAPayer(r.aPayer)
      setHistorique(r.historique)
      setErreur(null)
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Lecture impossible.')
    }
  }, [])

  useEffect(() => {
    void charger()
  }, [charger])

  async function generer() {
    if (!debut || !fin) {
      setErreur('Choisissez les deux dates de la période.')
      return
    }
    if (fin < debut) {
      setErreur('La date de fin est avant la date de début.')
      return
    }
    setOccupe(true)
    try {
      await calculerPaieLivreurs(debut, fin)
      await charger()
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Calcul impossible.')
    } finally {
      setOccupe(false)
    }
  }

  async function lancer() {
    if (!aPayer.length) {
      window.alert("Aucun reversement à verser — générez d'abord la quinzaine.")
      return
    }
    /* Cette console n'exécute AUCUN virement : aucune API Wave ou Orange n'est
       branchée. Marquer sans avoir viré laisse des livreurs sans leur paie, et
       la comptabilité affirme le contraire. */
    if (!window.confirm('Marquer TOUS les reversements livreurs en attente comme versés ?')) return
    setOccupe(true)
    try {
      await marquerLivreursPayes()
      await charger()
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Enregistrement impossible.')
    } finally {
      setOccupe(false)
    }
  }

  /* L'en-tête décrit ce que le TABLEAU contient, pas ce que la date du jour
     suggère : les deux pouvaient se contredire sans que rien ne le signale. */
  const bornes = aPayer.filter((p) => p.debut && p.fin)
  const min = bornes.length ? bornes.reduce((m, p) => (p.debut < m ? p.debut : m), bornes[0]!.debut) : null
  const max = bornes.length ? bornes.reduce((m, p) => (p.fin > m ? p.fin : m), bornes[0]!.fin) : null

  const totalVerser = aPayer.reduce((s, p) => s + p.total, 0)
  const totalCourses = aPayer.reduce((s, p) => s + p.courses, 0)
  const totalPrimes = aPayer.reduce((s, p) => s + p.bonusPointe + p.bonusVolume + p.bonusFidelite, 0)

  return (
    <>
      <div className="card">
        <div className="card-header">
          {min
            ? `Quinzaine affichée : ${jourCourt(min)} au ${jourCourt(max!)}`
            : 'Aucune quinzaine générée — choisissez la période ci-dessous, puis « Générer / recalculer »'}
        </div>
        <div className="card-body">
          <div className="stats" style={{ marginBottom: 14 }}>
            <div className="stat">
              <div className="label">Total a verser</div>
              <div className="val"><Compteur valeur={fcfa(totalVerser)} /></div>
            </div>
            <div className="stat">
              <div className="label">Total courses</div>
              <div className="val"><Compteur valeur={String(totalCourses)} /></div>
            </div>
            <div className="stat">
              <div className="label">Livreurs concernes</div>
              <div className="val"><Compteur valeur={String(aPayer.length)} /></div>
            </div>
            <div className="stat">
              <div className="label">Dont primes</div>
              <div className="val"><Compteur valeur={fcfa(totalPrimes)} /></div>
            </div>
          </div>

          {erreur && <p style={{ color: 'var(--danger)', fontSize: 13 }}>{erreur}</p>}

          <table>
            <thead>
              <tr>
                <th>Livreur</th>
                <th>Courses</th>
                <th>Base</th>
                {/* Les QUATRE primes séparément. Fusionnées, une paie contestée
                    devient invérifiable : le livreur dit « il manque la prime
                    de pluie », et personne ne peut ni confirmer ni infirmer. */}
                <th>Bonus Meteo</th>
                <th>Bonus Pointe</th>
                <th>Bonus Volume</th>
                <th>Bonus Fidel.</th>
                <th>Pourboires</th>
                <th>Total</th>
                <th>Statut</th>
              </tr>
            </thead>
            <tbody>
              {aPayer.map((p) => (
                <tr key={p.livreurId + p.debut}>
                  <td>{p.livreurNom}</td>
                  <td>{p.courses}</td>
                  <td>{fcfa(p.base)}</td>
                  <td>{fcfa(p.bonusMeteo)}</td>
                  <td>
                    <Prime montant={p.bonusPointe} detail={pluriel(p.coursesPointe, 'course', 'courses')} />
                  </td>
                  <td>
                    <Prime montant={p.bonusVolume} detail={pluriel(p.joursVolume, 'jour', 'jours')} />
                  </td>
                  <td>
                    <Prime montant={p.bonusFidelite} detail={pluriel(p.moisFidelite, 'mois', 'mois')} />
                  </td>
                  <td>{fcfa(p.pourboires)}</td>
                  <td>
                    <strong>{fcfa(p.total)}</strong>
                  </td>
                  <td>
                    <span className="badge badge-yellow">{p.statut}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <p style={{ fontSize: 12, color: 'var(--n400)', margin: '10px 0 0', lineHeight: 1.5 }}>
            Primes calculées par la base : pointe par course livrée dans une plage (montant figé à la
            livraison) · volume une fois par jour au seuil · fidélité une fois par mois au seuil, versée
            sur la période qui contient le dernier jour du mois. Une période en cours reste provisoire
            jusqu’à sa fin.
          </p>

          {periodes.length > 0 && jours && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 12, color: 'var(--n400)', marginBottom: 6 }}>
                Paie le {jours[0] === 1 ? '1er' : jours[0]} et le {jours[1]} du mois — choisissez la période à calculer :
              </div>
              <div className="chip-row" style={{ marginBottom: 0 }}>
                {periodes.map((p) => {
                  const choisie = p.debut === debut && p.fin === fin
                  return (
                    <button
                      key={p.debut}
                      className={`chip${choisie ? ' active' : ''}`}
                      title={p.terminee ? 'Période terminée : prête à être payée' : 'Période en cours : elle n’est pas terminée'}
                      onClick={() => {
                        setDebut(p.debut)
                        setFin(p.fin)
                      }}
                    >
                      {p.libelle}
                      {!p.terminee && <span className="chip-n">en cours</span>}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          <div
            style={{
              marginTop: 16,
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
            }}
          >
            <span style={{ fontSize: 12, color: 'var(--n400)' }}>Période :</span>
            <input
              type="date"
              value={debut}
              onChange={(e) => setDebut(e.target.value)}
              style={{ padding: '7px 10px', border: '1px solid var(--n200)', borderRadius: 12, fontSize: 12.5 }}
            />
            <span style={{ fontSize: 12, color: 'var(--n400)' }}>au</span>
            <input
              type="date"
              value={fin}
              onChange={(e) => setFin(e.target.value)}
              style={{ padding: '7px 10px', border: '1px solid var(--n200)', borderRadius: 12, fontSize: 12.5 }}
            />
            <button className="btn btn-outline btn-sm" disabled={occupe} onClick={() => void generer()}>
              Générer / recalculer
            </button>
            <button className="btn btn-green btn-sm" disabled={occupe} onClick={() => void lancer()}>
              Lancer les virements
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">Historique des periodes</div>
        <div className="card-body">
          <table>
            <thead>
              <tr>
                <th>Periode</th>
                <th>Livreurs</th>
                <th>Primes pointe</th>
                <th>Primes volume</th>
                <th>Primes fidélité</th>
                <th>Total verse</th>
                <th>Statut</th>
              </tr>
            </thead>
            <tbody>
              {historique.map((h) => (
                <tr key={h.periode}>
                  <td>{h.periode}</td>
                  <td>{h.livreurs}</td>
                  <td>{fcfa(h.bonusPointe)}</td>
                  <td>{fcfa(h.bonusVolume)}</td>
                  <td>{fcfa(h.bonusFidelite)}</td>
                  <td>{fcfa(h.total)}</td>
                  <td>
                    <span className="badge badge-green">payé</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}
